import { cn } from "#/lib/utils";
import { formatCompactNumber } from "../../lib/formatters.ts";
import type { ProcessedLevel } from "./hooks/use-orderbook.ts";
import { formatOrderbookPrice } from "./lib/format-price.ts";
import type { OrderbookSizeUnit } from "./types/orderbook.types.ts";

type OrderbookRowProps = {
	level: ProcessedLevel;
	side: "bid" | "ask";
	coin: string;
	markerCount: number;
	priceDecimals: number;
	sizeUnit: OrderbookSizeUnit;
	onPriceSelect: (price: string) => void;
};

export function OrderbookRow({
	level,
	side,
	markerCount,
	priceDecimals,
	sizeUnit,
	onPriceSelect,
}: OrderbookRowProps) {
	const isBid = side === "bid";
	const displaySz = sizeUnit === "token" ? level.sz : level.szUsdc;
	const displayTotal = sizeUnit === "token" ? level.total : level.totalUsdc;
	const markerClassName =
		markerCount > 0
			? isBid
				? "border-trade-green bg-trade-green"
				: "border-trade-red bg-trade-red"
			: "border-transparent bg-transparent";
	const markerLabel =
		markerCount > 0 ? `Open orders at ${level.px}: ${markerCount}` : undefined;

	return (
		<button
			type="button"
			title={markerLabel}
			className={cn(
				"relative grid h-5 sm:h-[20.5px] w-full shrink-0 cursor-pointer items-center transition-colors hover:bg-trade-interactive",
				"grid-cols-[1fr_1fr] px-1.5 text-label2-tabular-medium sm:grid-cols-[1fr_1fr_1fr] sm:px-3",
			)}
			onClick={() => onPriceSelect(level.px)}
		>
			<span
				className={`relative z-10 inline-flex items-center gap-0 sm:gap-1.5 ${isBid ? "text-trade-green" : "text-trade-red"}`}
			>
				<span
					aria-hidden="true"
					className={`hidden h-2 w-2 rounded-full border sm:inline-flex ${markerClassName}`}
				/>
				{formatOrderbookPrice(level.px, priceDecimals)}

				<span
					aria-hidden="true"
					className={`sm:hidden h-1 w-1 rounded-full border ${markerClassName}`}
				/>
			</span>
			<span className="relative z-10 text-right text-trade-text">
				{formatCompactNumber(displaySz)}
			</span>
			<span className="relative z-10 hidden text-right text-trade-text sm:block">
				{formatCompactNumber(displayTotal)}
			</span>

			<div
				className={`absolute inset-y-0 right-0 ${isBid ? "bg-trade-green-muted" : "bg-trade-red-muted"} transition-[width] duration-300 ease-in-out`}
				style={{ width: `${level.depth}%` }}
			/>
		</button>
	);
}
